import * as Popover from '@radix-ui/react-popover'
import { BubbleMenu, Editor } from '@tiptap/react'

import {
  AlignCenter,
  AlignJustify,
  AlignLeft,
  AlignRight,
  Bold,
  Italic,
  MoveVertical,
  RemoveFormatting,
  Sparkles,
  Strikethrough,
  Subscript,
  Superscript,
  Underline,
} from 'lucide-react'
import { memo, useState } from 'react'
import { AIEditorPanel } from '../../panels/AIEditorPanel/AIEditorPanel.js'
import { ColorPicker } from '../../panels/index.js'
import { Icon } from '../../ui/Icon.js'
import { Toolbar } from '../../ui/Toolbar.js'
import { ContentTypePicker } from './components/ContentTypePicker.js'
import { EditLinkPopover } from './components/EditLinkPopover.js'
// import { FontFamilyPicker } from "./components/FontFamilyPicker.js";
import i18next from 'i18next'
import { FontSizePicker } from './components/FontSizePicker.js'
import { useTextmenuCommands } from './hooks/useTextmenuCommands.js'
import { useTextmenuContentTypes } from './hooks/useTextmenuContentTypes.js'
import { useTextmenuStates } from './hooks/useTextmenuStates.js'

// We memorize the button so each button is not rerendered
// on every editor state change
const MemoButton = memo(Toolbar.Button)
const MemoColorPicker = memo(ColorPicker)
// const MemoFontFamilyPicker = memo(FontFamilyPicker);
const MemoFontSizePicker = memo(FontSizePicker)
const MemoContentTypePicker = memo(ContentTypePicker)

export type TextMenuProps = {
  editor: Editor
  dir: 'rtl' | 'ltr' | 'auto'
}

export const TextMenu = ({ editor, dir }: TextMenuProps) => {
  const commands = useTextmenuCommands(editor)
  const states = useTextmenuStates(editor)
  const blockOptions = useTextmenuContentTypes(editor)
  const [openAi, setOpenAi] = useState(false)

  return (
    <BubbleMenu
      tippyOptions={{
        placement: 'top-start',
        onHide: () => {
          setOpenAi(false)
        },
        popperOptions: {
          modifiers: [{ name: 'flip', enabled: false }],
        },
      }}
      editor={editor}
      pluginKey="textMenu"
      shouldShow={states.shouldShow}
      updateDelay={200}
    >
      {openAi ? (
        <AIEditorPanel
          editor={editor}
          onOpenChange={(value) => {
            setOpenAi(value)
          }}
        />
      ) : (
        <Toolbar.Wrapper>
          <Toolbar.Button
            className="text-purple-500 hover:text-purple-600 active:text-purple-600 dark:text-purple-400 dark:hover:text-purple-300 dark:active:text-purple-400"
            activeClassname="text-purple-600 hover:text-purple-600 dark:text-purple-400 dark:hover:text-purple-200"
            onClick={() => setOpenAi(true)}
          >
            <Icon icon={Sparkles} className="mr-1" />
            {i18next.t('aiTools') || 'AI Tools'}
            <span className="text-xs ml-1 px-2 py-1 bg-purple-100 text-purple-700 rounded-full dark:bg-purple-700 dark:text-purple-100 align-baseline">
              Beta
            </span>
          </Toolbar.Button>
          <Toolbar.Divider />
          <MemoContentTypePicker options={blockOptions} />
          {/* <MemoFontFamilyPicker
            onChange={commands.onSetFont}
            value={states.currentFont || ""}
          /> */}
          {/* <MemoFontSizePicker
            onChange={commands.onSetFontSize}
            value={states.currentSize || ""}
          /> */}
          <Toolbar.Divider />
          <MemoButton
            type="button"
            tooltip={i18next.t('boldText') || 'Bold'}
            tooltipShortcut={['Mod', 'B']}
            onClick={commands.onBold}
            active={states.isBold}
          >
            <Icon icon={Bold} />
          </MemoButton>
          <MemoButton
            type="button"
            tooltip={i18next.t('italicText') || 'Italic'}
            tooltipShortcut={['Mod', 'I']}
            onClick={commands.onItalic}
            active={states.isItalic}
          >
            <Icon icon={Italic} />
          </MemoButton>
          <MemoButton
            type="button"
            tooltip={i18next.t('underlineText') || 'Underline'}
            tooltipShortcut={['Mod', 'U']}
            onClick={commands.onUnderline}
            active={states.isUnderline}
          >
            <Icon icon={Underline} />
          </MemoButton>
          <EditLinkPopover onSetLink={commands.onLink} />
          {/* <Popover.Root>
            <Popover.Trigger asChild>
              <MemoButton
                type="button"
                active={!!states.currentHighlight}
                tooltip={i18next.t("highlightText", "Highlight text")}
              >
                <Icon icon={Highlighter} />
              </MemoButton>
            </Popover.Trigger>
            <Popover.Content side="top" sideOffset={8} asChild>
              <Surface className="p-1">
                <MemoColorPicker
                  color={states.currentHighlight}
                  onChange={commands.onChangeHighlight}
                  onClear={commands.onClearHighlight}
                />
              </Surface>
            </Popover.Content>
          </Popover.Root> */}
          {/* <Popover.Root>
            <Popover.Trigger asChild>
              <MemoButton
                type="button"
                active={!!states.currentColor}
                tooltip={i18next.t("textColor") || "Text color"}
              >
                <Icon icon={Palette} />
              </MemoButton>
            </Popover.Trigger>
            <Popover.Content side="top" sideOffset={8} asChild>
              <Surface className="p-1">
                <MemoColorPicker
                  color={states.currentColor}
                  onChange={commands.onChangeColor}
                  onClear={commands.onClearColor}
                />
              </Surface>
            </Popover.Content>
          </Popover.Root> */}
          <Popover.Root>
            <Popover.Trigger asChild>
              <MemoButton type="button" tooltip={i18next.t('moreOptions', 'More options')}>
                <Icon icon={MoveVertical} />
              </MemoButton>
            </Popover.Trigger>
            <Popover.Content side="top" asChild>
              <Toolbar.Wrapper>
                <MemoButton
                  type="button"
                  tooltip={i18next.t('subscript', 'Subscript')}
                  tooltipShortcut={['Mod', '.']}
                  onClick={commands.onSubscript}
                  active={states.isSubscript}
                >
                  <Icon icon={Subscript} />
                </MemoButton>
                <MemoButton
                  type="button"
                  tooltip={i18next.t('superscript', 'Superscript')}
                  tooltipShortcut={['Mod', ',']}
                  onClick={commands.onSuperscript}
                  active={states.isSuperscript}
                >
                  <Icon icon={Superscript} />
                </MemoButton>
                <Toolbar.Divider />
                <MemoButton
                  type="button"
                  tooltip={i18next.t('alignLeft') || 'Align left'}
                  tooltipShortcut={['Shift', 'Mod', 'L']}
                  onClick={commands.onAlignLeft}
                  active={states.isAlignLeft}
                >
                  <Icon icon={AlignLeft} />
                </MemoButton>
                <MemoButton
                  type="button"
                  tooltip={i18next.t('alignCenter') || 'Align center'}
                  tooltipShortcut={['Shift', 'Mod', 'E']}
                  onClick={commands.onAlignCenter}
                  active={states.isAlignCenter}
                >
                  <Icon icon={AlignCenter} />
                </MemoButton>
                <MemoButton
                  type="button"
                  tooltip={i18next.t('alignRight', 'Align right')}
                  tooltipShortcut={['Shift', 'Mod', 'R']}
                  onClick={commands.onAlignRight}
                  active={states.isAlignRight}
                >
                  <Icon icon={AlignRight} />
                </MemoButton>
                <MemoButton
                  type="button"
                  tooltip={i18next.t('alignJustify') || 'Justify'}
                  tooltipShortcut={['Shift', 'Mod', 'J']}
                  onClick={commands.onAlignJustify}
                  active={states.isAlignJustify}
                >
                  <Icon icon={AlignJustify} />
                </MemoButton>
                <MemoButton
                  type="button"
                  tooltip={i18next.t('strikethrough', 'Strikethrough')}
                  tooltipShortcut={['Mod', 'Shift', 'S']}
                  onClick={commands.onStrike}
                  active={states.isStrike}
                >
                  <Icon icon={Strikethrough} />
                </MemoButton>

                <MemoButton
                  type="button"
                  tooltip={i18next.t('clearFormatting') || 'Clear Formatting'}
                  onClick={commands.onClearFormatting}
                >
                  <Icon icon={RemoveFormatting} />
                </MemoButton>
              </Toolbar.Wrapper>
            </Popover.Content>
          </Popover.Root>
        </Toolbar.Wrapper>
      )}
    </BubbleMenu>
  )
}
